import type { ButtonProps } from '@timelyapp/react';
import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import { CrossInCircle } from 'components/Icons';
import RichTooltip from 'components/RichTooltip';
import React, { useCallback } from 'react';
import styles from './ClearableButton.module.scss';

interface Props extends Omit<ButtonProps, 'isActive'> {
  onClear?: (e: React.SyntheticEvent) => void;
  clearTooltip?: string;
  isActive?: boolean | null;
}

const stopEvent = (e: React.SyntheticEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const ClearableButton = React.forwardRef<HTMLButtonElement, Props>(
  ({ onClear, children, clearTooltip, isActive, ...buttonProps }, ref) => {
    const handleClear = useCallback(
      (e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onClear?.(e);
      },
      [onClear]
    );
    return (
      <Button
        {...buttonProps}
        isActive={isActive == null ? undefined : isActive}
        className={classNames(buttonProps.className, styles.buttonWrap)}
        caret={onClear != null ? false : buttonProps.caret}
        ref={ref}
      >
        {children}
        {onClear != null && (
          <span
            data-testid="clearable-button"
            onMouseDown={stopEvent}
            onClick={handleClear}
            className={classNames(styles.clearButton, 'ignore-react-onclickoutside', {
              [styles.active]: isActive,
            })}
          >
            <RichTooltip
              fixed
              position="top"
              tooltip={clearTooltip}
              childrenClassName={styles.iconWrapper}
            >
              <CrossInCircle />
            </RichTooltip>
          </span>
        )}
      </Button>
    );
  }
);

export default ClearableButton;
